import { SafeHtml } from '@angular/platform-browser';
import { TemplateRef } from '@angular/core';
import { DsButtonVariants, DsColors, DsPageMessageVariants, Icons, IconScale, IconStyles } from '@levelaccess/design-system';

import { $sortingOrder } from '@shared/constants/sort';
import { ICellConfig } from '../cells/base-cell/base-cell.component';

export interface IDclConfig<T extends ICellConfig> {
  type: any;
  config: T;
}

export interface ITableConfig {
  columns: { [key: string]: ITableColumn };
  caption: string;
  emptyState?: ITableEmptyState;
  singleSelect?: ISingleSelectOptions;
  styles?: Partial<Record<keyof CSSStyleDeclaration, string>>;
  // use this for min height and max height
  responsiveContainerStyles?: Partial<Record<keyof CSSStyleDeclaration, string>>;
  tableRole?: string;
}

export interface ITableColumn {
  translationKey: string;
  translationExtra?: string;
  hideLabel?: boolean;
  selectAllEnabled?: boolean;
  sortingEnabled?: boolean;
  styles?: Partial<Record<keyof CSSStyleDeclaration, string>>;
  tooltip?: {
    translationKey: string;
    icon?: Icons;
  };
  headerIcon?: {
    icon?: Icons;
    template?: TemplateRef<any>;
    color?: DsColors | string;
  };
}

export interface ISingleSelectOptions {
  selectable: boolean;
  selectionRadioButtons?: boolean;
  selectFirstRow?: boolean;
  radioButtonAriaLabelField?: string;
}

export interface ISelectedSingleData {
  row: ITableRow;
  index: number;
}

export interface ITableRow<T = any> {
  data: Record<string, IDclConfig<ICellConfig>>;
  originalData?: T;
  styles?: Partial<Record<keyof CSSStyleDeclaration, string>>;
  isHighlighted?: boolean;
  isDisabled?: boolean;
  isInactive?: boolean;
  key?: string;
}

export interface ITableDsIconConfig {
  icon: Icons;
  style?: IconStyles;
  scale?: IconScale;
  color?: DsColors | string;
  backgroundColor?: string;
  hasDefaultDsClass?: boolean;
}

export interface ITableDsButtonConfig {
  label: string;
  variant: DsButtonVariants;
  onClick: ($event?: Event) => any;
  icon?: Icons;
}

export type ITableEmptyState = ITableLegacyEmptyState | ITableDsPageMessageEmptyState;

export enum TableEmptyStateType {
  legacy = 'legacy',
  dsPageMessage = 'dsPageMessage',
}

interface ITableEmptyStateBase {
  iconId?: string;
  title?: string;
  titleHtml?: SafeHtml;
  subtitle?: string;

  iconHeight?: string;
  iconWidth?: string;
  iconFill?: string;

  button?: ITableEmptyStateGoToButtonLinkConfig;
  dsButton?: ITableDsButtonConfig;
  dsIcon?: ITableDsIconConfig;
}

interface ITableLegacyEmptyState extends ITableEmptyStateBase {
  type?: TableEmptyStateType.legacy;
  dsPageMessage?: never;
}

interface ITableDsPageMessageEmptyState extends Omit<ITableEmptyStateBase, 'iconId' | 'title' | 'titleHtml'> {
  type: TableEmptyStateType.dsPageMessage;
  title: string;
  dsPageMessage: ITableDsPageMessageConfig;
}

export interface ITableEmptyStateGoToButtonLinkConfig {
  label: string;
  routerLink: string[];
}

export interface ITableDsPageMessageConfig {
  icon: Icons;
  iconColor: DsColors;
  iconScale: IconScale;
  iconStyle: IconStyles;
  variant: DsPageMessageVariants;
}

export interface SelectAllEvent {
  selected: boolean;
}

export interface SortEvent {
  column: string;
  direction: $sortingOrder;
}
